import axios from 'axios';
import momentyz from 'moment-timezone';

// chiavi dedicate: riusare quelle del PartnerProgram farebbe sovrascrivere il token a chi apre
// entrambe le aree riservate nello stesso browser
const STORAGE = {
	token: 'affiliate_token',
	refresh: 'affiliate_refresh',
	expired: 'affiliate_expired',
};

const state = {
	token: localStorage.getItem(STORAGE.token) || '',
	refresh: localStorage.getItem(STORAGE.refresh) || '',
	expired: localStorage.getItem(STORAGE.expired) || '',
	id: '',
	affiliate: null,
	summary: null,
	clients: { data: [], pagination: { page: 1, itemsPerPage: 25, total: 0 } },
	compensations: { data: [], pagination: { page: 1, itemsPerPage: 25, total: 0 } },
};

const getters = {
	getToken: state => state.token,
	getRefresh: state => state.refresh,
	getExpired: state => state.expired,
	getAffiliate: state => state.affiliate,
	getSummary: state => state.summary,
	getClients: state => state.clients,
	getCompensations: state => state.compensations,
	isAuthenticated: state => {
		if (!state.token || !state.expired) {
			return false;
		}
		momentyz.tz.setDefault('Europe/Rome');
		const dateexpired = momentyz.utc(momentyz.unix(state.expired));
		const datenow = momentyz.utc(new Date());
		return datenow <= dateexpired;
	},
};

const mutations = {
	setToken: (state, value) => {
		state.token = value;
		if (value) localStorage.setItem(STORAGE.token, value);
		else localStorage.removeItem(STORAGE.token);
	},
	setRefresh: (state, value) => {
		state.refresh = value;
		if (value) localStorage.setItem(STORAGE.refresh, value);
		else localStorage.removeItem(STORAGE.refresh);
	},
	setExpired: (state, value) => {
		state.expired = value;
		if (value) localStorage.setItem(STORAGE.expired, value);
		else localStorage.removeItem(STORAGE.expired);
	},
	setId: (state, value) => {
		state.id = value;
	},
	setAffiliate: (state, value) => {
		state.affiliate = value;
	},
	setSummary: (state, value) => {
		state.summary = value;
	},
	setClients: (state, value) => {
		state.clients = value || { data: [], pagination: { page: 1, itemsPerPage: 25, total: 0 } };
	},
	setCompensations: (state, value) => {
		state.compensations = value || { data: [], pagination: { page: 1, itemsPerPage: 25, total: 0 } };
	},
	// token nuovi dopo cambio password / "esci da tutti gli altri dispositivi": i vecchi sono revocati
	setSession: (state, session) => {
		if (!session || !session.accessToken) {
			return;
		}
		state.token = session.accessToken;
		state.refresh = session.refreshToken;
		state.expired = session.expired;
		localStorage.setItem(STORAGE.token, session.accessToken);
		localStorage.setItem(STORAGE.refresh, session.refreshToken);
		localStorage.setItem(STORAGE.expired, session.expired);
	},
	setLogout: state => {
		state.token = '';
		state.refresh = '';
		state.expired = '';
		state.id = '';
		state.affiliate = null;
		state.summary = null;
		state.clients = { data: [], pagination: { page: 1, itemsPerPage: 25, total: 0 } };
		state.compensations = { data: [], pagination: { page: 1, itemsPerPage: 25, total: 0 } };
		localStorage.removeItem(STORAGE.token);
		localStorage.removeItem(STORAGE.refresh);
		localStorage.removeItem(STORAGE.expired);
	},
};

/** Chiamata autenticata: il token viene passato a mano, non c'e' un interceptor globale. */
const authGet = async (context, url, params = {}) => {
	const ok = await context.dispatch('ensureFreshToken');
	if (!ok) {
		context.commit('setLogout');
		return null;
	}
	try {
		const response = await axios.get(`${context.rootGetters['settings/getUrl']}${url}`, {
			headers: { Authorization: `Bearer ${context.state.token}` },
			params: params,
		});
		return response.status === 201 ? response.data : null;
	} catch (error) {
		if (error.response && error.response.status === 401) {
			context.commit('setLogout');
		}
		return null;
	}
};

const authPatch = async (context, url, payload) => {
	const ok = await context.dispatch('ensureFreshToken');
	if (!ok) {
		return { success: false };
	}
	try {
		const response = await axios.patch(`${context.rootGetters['settings/getUrl']}${url}`, payload, {
			headers: { Authorization: `Bearer ${context.state.token}` },
		});
		return response.status === 201 ? { success: true, data: response.data.data } : { success: false, message: response.data && response.data.message };
	} catch (error) {
		return { success: false, message: error.response && error.response.data && error.response.data.message };
	}
};

const actions = {
	login: async (context, payload) => {
		const url = 'affiliate-auth/login';
		try {
			const response = await axios.post(`${context.rootGetters['settings/getUrl']}${url}`, payload);
			if (response.status === 201) {
				context.commit('setToken', response.data.accessToken);
				context.commit('setRefresh', response.data.refreshToken);
				context.commit('setExpired', response.data.expired);
				context.commit('setId', response.data.id);
			}
			return response;
		} catch (error) {
			return error.response || { status: 500, data: { message: 'network_error' } };
		}
	},
	refreshToken: async context => {
		const url = 'affiliate-auth/token';
		const refresh = context.state.refresh || localStorage.getItem(STORAGE.refresh);
		if (!refresh) {
			return { status: false };
		}
		try {
			const response = await axios.post(`${context.rootGetters['settings/getUrl']}${url}`, { token: refresh });
			if (response.status === 201) {
				context.commit('setToken', response.data.accessToken);
				context.commit('setExpired', response.data.expired);
				return { status: true, token: response.data.accessToken };
			}
			return { status: false };
		} catch (error) {
			return { status: false };
		}
	},
	ensureFreshToken: async context => {
		if (!context.state.token) {
			return false;
		}
		momentyz.tz.setDefault('Europe/Rome');
		const dateexpired = momentyz.utc(momentyz.unix(context.state.expired));
		const datenow = momentyz.utc(new Date());
		if (datenow > dateexpired) {
			const result = await context.dispatch('refreshToken');
			return !!result.status;
		}
		return true;
	},
	fetchMe: async context => {
		const payload = await authGet(context, 'affiliate-program/me');
		if (payload) {
			context.commit('setAffiliate', payload.data);
			return payload.data;
		}
		return null;
	},
	fetchSummary: async context => {
		const payload = await authGet(context, 'affiliate-program/summary');
		if (payload) {
			context.commit('setSummary', payload.data);
			return payload.data;
		}
		return null;
	},
	fetchClients: async (context, params) => {
		const payload = await authGet(context, 'affiliate-program/clients', params || {});
		if (payload) {
			context.commit('setClients', payload);
			return payload;
		}
		return null;
	},
	fetchCompensations: async (context, params) => {
		const payload = await authGet(context, 'affiliate-program/compensations', params || {});
		if (payload) {
			context.commit('setCompensations', payload);
			return payload;
		}
		return null;
	},
	updateProfile: async (context, payload) => {
		const result = await authPatch(context, 'affiliate-program/profile', payload);
		if (result.success) {
			context.commit('setAffiliate', result.data);
		}
		return result;
	},
	changePassword: async (context, payload) => {
		const result = await authPatch(context, 'affiliate-program/password', payload);
		if (result.success) {
			context.commit('setSession', result.data);
		}
		return result;
	},
	logoutAll: async context => {
		const ok = await context.dispatch('ensureFreshToken');
		if (!ok) {
			return { success: false };
		}
		try {
			const response = await axios.post(`${context.rootGetters['settings/getUrl']}affiliate-program/logout-all`, {}, {
				headers: { Authorization: `Bearer ${context.state.token}` },
			});
			if (response.status === 201) {
				context.commit('setSession', response.data && response.data.data);
				return { success: true };
			}
			return { success: false };
		} catch (error) {
			return { success: false, message: error.response && error.response.data && error.response.data.message };
		}
	},
	logout: context => {
		context.commit('setLogout');
	},
};

export default {
	namespaced: true,
	state,
	mutations,
	getters,
	actions,
};
